import { getApps, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { auth, firebaseConfig } from '../firebase-config';

const SUPPORT_APP_NAME = 'hrfamly-support-admin';
const RETURN_PATH_KEY = 'hrfamly.support-return-path';
let supportAuth;
let switching = false;

function getSupportAuth() {
  if (!supportAuth) {
    const app = getApps().find((candidate) => candidate.name === SUPPORT_APP_NAME) ||
      initializeApp(firebaseConfig, SUPPORT_APP_NAME);
    // Use Firebase-managed persistence, like the original login. Reloads and
    // browser restarts must not strand support inside the customer account.
    supportAuth = initializeAuth(app, {
      persistence: browserLocalPersistence,
    });
  }
  return supportAuth;
}

function clearLegacyMarker() {
  try {
    localStorage.removeItem('superAdminUUID');
  } catch {
    // Legacy UID storage is never used as authorization.
  }
}

function rememberReturnPath() {
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname + window.location.search);
  } catch {
    // Returning to the Admin list is a sufficient fallback.
  }
}

function takeReturnPath() {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    const url = new URL(path || '/admin', window.location.origin);
    if (url.origin === window.location.origin && /^\/admin(?:\/|$)/.test(url.pathname)) {
      return url.pathname + url.search;
    }
  } catch {
    // Missing or modified navigation metadata must not cause an external redirect.
  }
  return '/admin';
}

async function requestCustomTokens(path, user, body) {
  const token = await user.getIdToken(true);
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body && { body: JSON.stringify(body) }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Support-login kunne ikke gennemføres. Prøv igen.');
  return data;
}

export async function startSupportSession(targetUid) {
  if (switching) return;
  switching = true;
  const actor = auth.currentUser;
  let retainedAdmin = false;
  try {
    if (!actor) throw new Error('Log ind som superadmin først.');
    const adminAuth = getSupportAuth();
    await adminAuth.authStateReady();
    const data = await requestCustomTokens('/api/admin/emulateuser', actor, { userUUID: targetUid });
    if (!data.customToken || !data.adminCustomToken) throw new Error('Support-login kunne ikke startes. Prøv igen.');
    // Explicitly require persistent storage; never switch the customer login if
    // Firebase had to fall back to an admin session that disappears on reload.
    await setPersistence(adminAuth, browserLocalPersistence);
    const backup = await signInWithCustomToken(adminAuth, data.adminCustomToken);
    retainedAdmin = true;
    if (backup.user.uid !== actor.uid) throw new Error('Superadmin-sessionen kunne ikke bekræftes.');
    rememberReturnPath();
    // Do not leave the admin account until its return session is ready.
    await signInWithCustomToken(auth, data.customToken);
    clearLegacyMarker();
  } catch (error) {
    if (retainedAdmin && auth.currentUser?.uid === actor.uid && supportAuth?.currentUser) {
      await signOut(supportAuth).catch(() => {});
    }
    throw error;
  } finally {
    switching = false;
  }
  // Reset company data and subscriptions when changing identity.
  window.location.assign('/');
}

export async function restoreSupportSession() {
  if (switching) return;
  switching = true;
  try {
    const adminAuth = getSupportAuth();
    await adminAuth.authStateReady();
    if (!adminAuth.currentUser) throw new Error('Supportsessionen er udløbet. Log ind som superadmin igen.');
    const data = await requestCustomTokens('/api/admin/revertsuperadmin', adminAuth.currentUser);
    if (!data.customToken) throw new Error('Kunne ikke skifte tilbage. Prøv igen.');
    await signInWithCustomToken(auth, data.customToken);
    await signOut(adminAuth);
    clearLegacyMarker();
  } finally {
    switching = false;
  }
  window.location.assign(takeReturnPath());
}

export function subscribeToSupportAdmin(callback) {
  const adminAuth = getSupportAuth();
  let cancelled = false;
  let unsubscribers = [];
  Promise.all([auth.authStateReady(), adminAuth.authStateReady()]).then(() => {
    if (cancelled) return;
    const notify = () => {
      if (!auth.currentUser) {
        callback(null);
        // Also clear the retained login after a logout in another browser tab.
        if (adminAuth.currentUser) signOut(adminAuth).catch(console.error);
      } else {
        callback(adminAuth.currentUser);
      }
    };
    unsubscribers = [onAuthStateChanged(auth, notify), onAuthStateChanged(adminAuth, notify)];
  }).catch((error) => {
    console.error('Unable to restore support session:', error);
    if (!cancelled) callback(null);
  });
  return () => {
    cancelled = true;
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}

export async function clearSupportSession() {
  const adminAuth = getSupportAuth();
  await adminAuth.authStateReady();
  await signOut(adminAuth);
  clearLegacyMarker();
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Navigation metadata is not a credential.
  }
}
